import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ---- Receiving ----
export function useExpectedDeliveries() {
  return useQuery({
    queryKey: ['receiving', 'expected'],
    queryFn: async () => (await api.get('/api/receiving/expected')).data.orders,
    staleTime: 0,
  });
}

export function usePoForReceiving(id) {
  return useQuery({
    queryKey: ['receiving', 'po', String(id)],
    queryFn: async () => (await api.get(`/api/receiving/po/${id}`)).data,
    enabled: !!id,
    staleTime: 0,
  });
}

export function useReceipts(params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));
  const qs = new URLSearchParams(clean).toString();
  return useQuery({
    queryKey: ['receiving', 'receipts', clean],
    queryFn: async () => (await api.get(`/api/receiving/receipts${qs ? `?${qs}` : ''}`)).data.receipts,
    staleTime: 0,
  });
}

export function useReceipt(id) {
  return useQuery({
    queryKey: ['receiving', 'receipt', String(id)],
    queryFn: async () => (await api.get(`/api/receiving/receipts/${id}`)).data,
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/receiving/receipts', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['receiving'] }),
  });
}

export function useSaveReceiptLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, lines }) => (await api.patch(`/api/receiving/receipts/${id}/lines`, { lines })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['receiving'] }),
  });
}

export function useConfirmReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, storeroom_id, note }) => (await api.post(`/api/receiving/receipts/${id}/confirm`, { storeroom_id, note })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receiving'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ---- Inventory ----
export function useStorerooms() {
  return useQuery({
    queryKey: ['inventory', 'storerooms'],
    queryFn: async () => (await api.get('/api/inventory/storerooms')).data.storerooms,
    staleTime: 300000,
  });
}

export function useInventory(params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));
  const qs = new URLSearchParams(clean).toString();
  return useQuery({
    queryKey: ['inventory', 'list', clean],
    queryFn: async () => (await api.get(`/api/inventory${qs ? `?${qs}` : ''}`)).data.items,
    keepPreviousData: true,
  });
}

// Ordered and not yet accepted. Polled rather than cached hard: a receipt booked
// on another screen changes this list, and a stale back order is a phone call to
// a supplier who already delivered.
export function useBackOrders(params = {}) {
  const qs = new URLSearchParams(Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v != null))).toString();
  return useQuery({
    queryKey: ['backOrders', params],
    queryFn: async () => (await api.get(`/api/receiving/back-orders${qs ? `?${qs}` : ''}`)).data,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// ---- Purchase orders: settling them ----
//
// Receiving no longer closes a PO (see receivingService.confirmReceipt in tech).
// A fully-counted order sits here until logistics decides it is settled, because
// damaged and rejected units still owe us a replacement or a credit note.
export function useOpenPurchaseOrders() {
  return useQuery({
    queryKey: ['purchaseOrders', 'open'],
    queryFn: async () => (await api.get('/api/purchase-orders/open')).data,
    staleTime: 0,
  });
}

function useSettleMutation(path) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await api.post(`/api/purchase-orders/${id}/${path}`, body)).data,
    // Closing an order changes what the storeman sees and what Back Orders
    // counts, so all three views have to be refetched, not just this one.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] });
      qc.invalidateQueries({ queryKey: ['receiving'] });
      qc.invalidateQueries({ queryKey: ['backOrders'] });
    },
  });
}

export const useClosePurchaseOrder = () => useSettleMutation('close');
export const useReopenPurchaseOrder = () => useSettleMutation('reopen');

// Delivery-note photos for a receipt. Fetched only when a receipt is opened:
// they come back as base64 data URLs, so pulling them for a whole list would
// move megabytes nobody asked for.
export function usePodPhotos(grnId) {
  return useQuery({
    enabled: Boolean(grnId),
    queryKey: ['receiving', 'pod', String(grnId)],
    queryFn: async () => (await api.get(`/api/receiving/receipts/${grnId}/pod`)).data,
    // A receipt with no photo answers 404, which is a normal state here and
    // not worth three retries.
    retry: false,
    staleTime: 600000,
  });
}
